<template>
  <div class="project-summary-page">
    <div class="page-head">
      <div>
        <h2>项目汇总</h2>
        <p>按后台配置从数据库业务表生成包名汇总、汇总结果和更多自定义报表。</p>
      </div>
      <div class="head-actions">
        <el-button size="mini" icon="el-icon-refresh" :loading="loading" @click="refreshCurrent">刷新</el-button>
        <el-button size="mini" icon="el-icon-s-data" @click="openDatasetDialog">数据字典</el-button>
        <el-button
          size="mini"
          icon="el-icon-document-copy"
          :disabled="!activeReportPage"
          :loading="pageCopying"
          @click="copyCurrentReportPage"
        >复制报表</el-button>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="openNewReportDialog">新增报表</el-button>
      </div>
    </div>

    <section class="query-panel">
      <el-tabs v-model="activeTab" type="card" @tab-click="handleTabChange">
        <el-tab-pane
          v-for="page in reportPages"
          :key="page.code"
          :label="page.pageName"
          :name="page.code"
        />
        <el-tab-pane label="游戏配置" name="configs" />
      </el-tabs>

      <el-form v-if="isReportTab" :inline="true" size="mini" class="query-form" @submit.native.prevent>
        <el-form-item label="日期">
          <el-date-picker
            v-model="reportQuery.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 260px"
          />
        </el-form-item>
        <el-form-item v-if="hasDateGroup" label="日期分组">
          <el-select
            v-model="reportQuery.dateGranularity"
            style="width: 92px"
            @change="handleDateGranularityChange"
          >
            <el-option label="按日" value="day" :disabled="activeRequiresMonthly" />
            <el-option label="按月" value="month" />
            <el-option label="按年" value="year" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="hasField('gameName')" label="游戏">
          <el-input
            v-model.trim="reportQuery.gameName"
            clearable
            placeholder="游戏名称"
            style="width: 170px"
            @keyup.enter.native="searchReport"
          />
        </el-form-item>
        <el-form-item v-if="hasField('platform')" label="平台">
          <el-input
            v-model.trim="reportQuery.platform"
            clearable
            placeholder="平台"
            style="width: 120px"
            @keyup.enter.native="searchReport"
          />
        </el-form-item>
        <el-form-item v-if="hasField('packageName')" label="包名">
          <el-input
            v-model.trim="reportQuery.packageName"
            clearable
            placeholder="包名"
            style="width: 180px"
            @keyup.enter.native="searchReport"
          />
        </el-form-item>
        <el-form-item label="关键词">
          <el-input
            v-model.trim="reportQuery.keyword"
            clearable
            placeholder="游戏、平台、包名"
            style="width: 220px"
            @keyup.enter.native="searchReport"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="reportLoading" @click="searchReport">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetReport">重置</el-button>
          <el-button icon="el-icon-setting" @click="openReportPageDialog">页面配置</el-button>
          <el-button
            icon="el-icon-download"
            :loading="reportExporting"
            :disabled="reportPage.total === 0"
            @click="exportReport"
          >导出Excel</el-button>
          <el-button
            v-if="isManualAmountTab"
            icon="el-icon-document"
            :loading="manualTemplateDownloading"
            @click="downloadManualTemplate"
          >下载模板</el-button>
          <el-upload
            v-if="isManualAmountTab"
            class="manual-import-upload"
            action=""
            accept=".xls,.xlsx"
            :show-file-list="false"
            :http-request="importManualAmount"
          >
            <el-button type="success" icon="el-icon-upload2" :loading="manualImporting">导入手动数据</el-button>
          </el-upload>
        </el-form-item>
      </el-form>

      <el-form v-else :inline="true" size="mini" class="query-form" @submit.native.prevent>
        <el-form-item label="关键词">
          <el-input
            v-model.trim="configQuery.keyword"
            clearable
            placeholder="游戏、平台、包名、备注"
            style="width: 260px"
            @keyup.enter.native="searchConfigs"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="configLoading" @click="searchConfigs">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetConfigs">重置</el-button>
          <el-button type="primary" icon="el-icon-plus" @click="openConfigDialog()">新增配置</el-button>
        </el-form-item>
      </el-form>
    </section>

    <section v-if="isReportTab" class="table-panel">
      <div class="table-toolbar">
        <span>数据源：{{ activeDatasetName }}</span>
        <span class="muted">汇总行按所有符合条件的数据计算，不受当前分页影响。</span>
      </div>
      <el-table
        ref="reportTable"
        :key="activeTab"
        :data="reportDisplayRows"
        size="mini"
        border
        stripe
        :height="tableHeight"
        v-loading="reportLoading"
        :row-class-name="reportRowClass"
        :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }"
        @sort-change="handleReportSortChange"
      >
        <el-table-column
          v-for="column in reportColumns"
          :key="column.key"
          :prop="column.key"
          :label="column.label"
          :min-width="column.width || columnWidth(column)"
          sortable="custom"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span>{{ formatCell(row[column.key], column) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ formatInteger(reportPage.total) }} 条</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="reportPage.page"
          :page-size="reportPage.pageSize"
          :page-sizes="[30, 50, 100, 200, 500]"
          :total="reportPage.total"
          @size-change="handleReportSizeChange"
          @current-change="handleReportCurrentChange"
        />
      </div>
    </section>

    <section v-else class="table-panel">
      <div class="table-toolbar">
        <span>游戏配置</span>
        <span class="muted">游戏名称、平台、包名三项组合唯一，供报表关联包名使用。</span>
      </div>
      <el-table
        :data="configRows"
        size="mini"
        border
        stripe
        :height="tableHeight"
        v-loading="configLoading"
        :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }"
      >
        <el-table-column prop="gameName" label="游戏名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="platform" label="平台" width="120" show-overflow-tooltip />
        <el-table-column prop="packageName" label="包名" min-width="180" show-overflow-tooltip />
        <el-table-column prop="enabled" label="启用" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="Number(row.enabled) === 1 ? 'success' : 'info'">
              {{ Number(row.enabled) === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="220" show-overflow-tooltip />
        <el-table-column prop="updatedAt" label="更新时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.updatedAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openConfigDialog(row)">编辑</el-button>
            <el-button type="text" size="mini" class="danger-link" @click="deleteConfig(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ formatInteger(configPage.total) }} 条</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="configPage.page"
          :page-size="configPage.pageSize"
          :page-sizes="[30, 50, 100, 200]"
          :total="configPage.total"
          @size-change="handleConfigSizeChange"
          @current-change="handleConfigCurrentChange"
        />
      </div>
    </section>

    <el-dialog
      title="数据库表及字段配置"
      :visible.sync="datasetDialogVisible"
      width="1320px"
      append-to-body
    >
      <el-form :model="datasetForm" label-width="112px" size="small" class="dataset-config-form">
        <el-row :gutter="12">
          <el-col :span="7">
            <el-form-item label="数据表">
              <div class="dataset-select-row">
                <el-select
                  v-model="datasetForm.id"
                  filterable
                  style="width: 100%"
                  @change="handleDictionaryDatasetChange"
                >
                  <el-option
                    v-for="dataset in datasets"
                    :key="dataset.id"
                    :label="dataset.name"
                    :value="Number(dataset.id)"
                  />
                </el-select>
                <el-button type="primary" icon="el-icon-plus" @click="openNewDatasetForm">新增</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="表中文名">
              <el-input v-model.trim="datasetForm.name" placeholder="请输入数据库表中文名" />
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item label="数据表编码">
              <el-input v-model.trim="datasetForm.code" :disabled="!!datasetForm.id" placeholder="英文、数字或下划线" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="数据库连接">
              <el-select v-model="datasetForm.connectionCode" style="width: 100%" :disabled="!!datasetForm.id">
                <el-option
                  v-for="connection in datasetConnections"
                  :key="connection.code"
                  :label="connection.name"
                  :value="connection.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="7">
            <el-form-item label="数据库表名">
              <el-input v-model.trim="datasetForm.sourceTable" :disabled="!!datasetForm.id" placeholder="请输入数据库表名" />
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item label="时间粒度">
              <el-radio-group v-model="datasetForm.timeGranularity">
                <el-radio-button label="day">按日</el-radio-button>
                <el-radio-button label="month">按月</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col v-if="datasetForm.timeGranularity === 'month'" :span="6">
            <el-form-item label="月份存储方式">
              <el-select v-model="datasetForm.monthDateMode" style="width: 100%">
                <el-option label="单个月份字段" value="single" />
                <el-option label="周期开始和结束" value="range" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="datasetForm.timeGranularity === 'day' || datasetForm.monthDateMode === 'single'" :span="6">
            <el-form-item :label="datasetForm.timeGranularity === 'day' ? '日期字段' : '月份字段'">
              <el-input v-model.trim="datasetForm.dateColumn" placeholder="数据库字段名" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="datasetForm.timeGranularity === 'month' && datasetForm.monthDateMode === 'range'" :gutter="12">
          <el-col :span="7">
            <el-form-item label="周期开始字段">
              <el-input v-model.trim="datasetForm.periodStartColumn" placeholder="例如 start_date" />
            </el-form-item>
          </el-col>
          <el-col :span="7">
            <el-form-item label="周期结束字段">
              <el-input v-model.trim="datasetForm.periodEndColumn" placeholder="例如 end_date" />
            </el-form-item>
          </el-col>
          <el-col :span="10">
            <el-form-item label="归属月份">
              <el-select v-model="datasetForm.periodAssignment" style="width: 100%">
                <el-option label="按周期结束日期所在月（推荐）" value="end_month" />
                <el-option label="按周期开始日期所在月" value="start_month" />
                <el-option label="按覆盖天数最多的月份" value="majority_month" />
                <el-option label="按各月覆盖天数分摊" value="split_days" />
              </el-select>
              <div class="form-help">
                查询时先确定结算单归属月份，再与日表的月汇总结果匹配；不会把月结算单当成日数据。通常选择结束月，只有确需跨月分摊时才选择按天数分摊。
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="6">
            <el-form-item label="游戏字段">
              <el-input v-model.trim="datasetForm.gameColumn" placeholder="数据库字段名" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="游戏平台存储">
              <el-select v-model="datasetForm.gameStorageMode" style="width: 100%">
                <el-option label="游戏、平台分列存储" value="separate" />
                <el-option label="平台写在游戏名后缀" value="suffix" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="datasetForm.gameStorageMode === 'separate'" :span="6">
            <el-form-item label="平台字段">
              <el-input v-model.trim="datasetForm.platformColumn" placeholder="数据库字段名" />
            </el-form-item>
          </el-col>
          <el-col v-else :span="6">
            <el-form-item label="无后缀平台">
              <el-input v-model.trim="datasetForm.defaultPlatform" placeholder="例如：微信" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="包名字段">
              <el-input v-model.trim="datasetForm.packageColumn" placeholder="可留空，按游戏配置匹配" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="datasetForm.gameStorageMode === 'suffix'" :gutter="12">
          <el-col :span="24">
            <el-form-item label="平台后缀规则">
              <div class="suffix-rule-list">
                <div v-for="(rule, index) in datasetForm.suffixRules" :key="index" class="suffix-rule-row">
                  <el-input v-model.trim="rule.suffix" placeholder="后缀，如：(抖音)" />
                  <span>识别为</span>
                  <el-input v-model.trim="rule.platform" placeholder="平台，如：抖音" />
                  <el-button icon="el-icon-delete" @click="removeSuffixRule(index)" />
                </div>
                <el-button size="mini" icon="el-icon-plus" @click="addSuffixRule">新增后缀</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div class="dataset-field-toolbar">
        <el-alert
          title="新增字段需先检查数据库字段是否存在，检查通过后才能保存。"
          type="info"
          :closable="false"
          show-icon
        />
        <el-button type="primary" size="mini" icon="el-icon-plus" :disabled="!datasetForm.id" @click="addDatasetField">新增字段</el-button>
      </div>

      <el-table :data="datasetForm.fields" size="mini" border max-height="480">
        <el-table-column prop="columnName" label="数据库字段" min-width="230">
          <template slot-scope="{ row }">
            <el-input
              v-if="row.isNew"
              v-model.trim="row.columnName"
              size="mini"
              placeholder="请输入英文字段名"
              @input="handleDatasetColumnInput(row)"
              @blur="validateDatasetField(row)"
              @keyup.enter.native="validateDatasetField(row, true)"
            >
              <el-button
                slot="append"
                icon="el-icon-search"
                :loading="row.validating"
                title="检查字段"
                @click="validateDatasetField(row, true)"
              />
            </el-input>
            <span v-else>{{ row.sourceFieldName || dictionaryColumnName(row.columnName) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="fieldKey" label="内部字段" min-width="160">
          <template slot-scope="{ row }">
            <el-input
              v-model.trim="row.fieldKey"
              size="mini"
              placeholder="如 cashCost"
              @input="markDatasetFieldKeyManual(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="字段中文名" min-width="180">
          <template slot-scope="{ row }">
            <el-input v-model.trim="row.displayName" size="mini" />
          </template>
        </el-table-column>
        <el-table-column label="类型" width="110">
          <template slot-scope="{ row }">
            <el-select v-model="row.dataType" size="mini" style="width: 100%">
              <el-option label="文本" value="string" />
              <el-option label="数字" value="number" />
              <el-option label="日期" value="date" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="可分组" width="90" align="center">
          <template slot-scope="{ row }">
            <el-switch v-model="row.groupable" @change="syncDictionaryField(row)" />
          </template>
        </el-table-column>
        <el-table-column label="默认聚合" width="130">
          <template slot-scope="{ row }">
            <el-select v-model="row.defaultAggregate" size="mini" style="width: 100%" :disabled="row.groupable">
              <el-option v-if="row.groupable" label="分组" value="group" />
              <el-option label="求和" value="sum" />
              <el-option label="平均" value="avg" />
              <el-option label="最小" value="min" />
              <el-option label="最大" value="max" />
              <el-option label="计数" value="count" />
              <el-option label="去重计数" value="count_distinct" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="检查结果" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="!row.isNew" size="mini" type="info">已配置</el-tag>
            <el-tag v-else-if="row.validationStatus === 'exists'" size="mini" type="success">字段存在</el-tag>
            <el-tag v-else-if="row.validationStatus === 'duplicate'" size="mini" type="warning">已配置</el-tag>
            <el-tag v-else-if="row.validationStatus === 'missing'" size="mini" type="danger">字段不存在</el-tag>
            <el-tag v-else-if="row.validationStatus === 'error'" size="mini" type="danger">检查失败</el-tag>
            <el-tag v-else-if="row.validationStatus === 'checking'" size="mini">检查中</el-tag>
            <el-tag v-else size="mini" type="warning">未检查</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70" fixed="right" align="center">
          <template slot-scope="{ row, $index }">
            <el-button
              v-if="row.isNew"
              type="text"
              size="mini"
              class="danger-link"
              @click="removeDatasetField($index)"
            >删除</el-button>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

      <div slot="footer">
        <el-button size="small" @click="datasetDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="datasetSaving"
          :disabled="hasUnconfirmedNewDatasetFields"
          @click="saveDatasetDictionary"
        >{{ datasetForm.id ? '保存' : '创建数据表' }}</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="pageForm.id ? '页面配置' : '新增报表页'"
      :visible.sync="pageDialogVisible"
      width="1180px"
      append-to-body
    >
      <el-form :model="pageForm" label-width="96px" size="small" class="page-config-form">
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="页面名称">
              <el-input v-model.trim="pageForm.pageName" placeholder="例如：包名汇总" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="页面编码">
              <el-input v-model.trim="pageForm.code" :disabled="!!pageForm.id" placeholder="英文或数字，下划线" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="数据源">
              <el-select
                v-model="pageForm.datasetIds"
                multiple
                filterable
                collapse-tags
                style="width: 100%"
                placeholder="可选择多个数据表"
                @change="handleDatasetChange"
              >
                <el-option
                  v-for="dataset in datasets"
                  :key="dataset.id"
                  :label="dataset.name"
                  :value="Number(dataset.id)"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="10">
            <el-form-item label="默认分组">
              <el-select v-model="pageForm.defaultGroupBy" multiple filterable style="width: 100%">
                <el-option
                  v-for="field in pageGroupFields"
                  :key="field.fieldKey"
                  :label="field.formulaName || field.displayName"
                  :value="field.fieldKey"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item label="排序字段">
              <el-select v-model="pageForm.defaultSortField" clearable filterable style="width: 100%">
                <el-option
                  v-for="field in pageFields"
                  :key="field.fieldKey"
                  :label="field.formulaName || field.displayName"
                  :value="field.fieldKey"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item label="排序方向">
              <el-select v-model="pageForm.defaultSortDir" style="width: 100%">
                <el-option label="升序" value="asc" />
                <el-option label="降序" value="desc" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="3">
            <el-form-item label="启用">
              <el-switch v-model="pageForm.enabled" />
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <el-form-item label="排序">
              <el-input-number v-model="pageForm.sortOrder" :min="0" :controls="false" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div class="column-toolbar">
        <span>报表列</span>
        <el-button size="mini" type="primary" icon="el-icon-plus" @click="addPageColumn">新增列</el-button>
      </div>
      <el-table :data="pageColumns" size="mini" border max-height="360">
        <el-table-column label="显示名" min-width="140">
          <template slot-scope="{ row }">
            <el-input v-model.trim="row.displayName" size="mini" />
          </template>
        </el-table-column>
        <el-table-column label="列类型" width="122">
          <template slot-scope="{ row }">
            <el-radio-group
              v-model="row.columnMode"
              size="mini"
              class="column-mode-group"
              @change="changeColumnMode(row)"
            >
              <el-radio-button label="field">字段</el-radio-button>
              <el-radio-button label="formula">公式</el-radio-button>
            </el-radio-group>
          </template>
        </el-table-column>
        <el-table-column label="字段" min-width="170">
          <template slot-scope="{ row }">
            <el-select
              v-model="row.fieldKey"
              clearable
              filterable
              size="mini"
              style="width: 100%"
              :disabled="row.columnMode === 'formula'"
              :placeholder="row.columnMode === 'formula' ? '公式列无需字段' : '请选择字段'"
              @change="syncColumnByField(row)"
            >
              <el-option
                v-for="field in pageFields"
                :key="field.fieldKey"
                :label="field.formulaName || field.displayName"
                :value="field.fieldKey"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="公式" min-width="620">
          <template slot-scope="{ row }">
            <div v-if="row.columnMode === 'formula'" class="formula-editor">
              <el-input
                :ref="`formulaInput_${row.editorKey}`"
                v-model.trim="row.formula"
                size="mini"
                class="formula-input"
                placeholder="[利润] - [CP分成]"
                @input="syncFormulaColumn(row)"
                @focus="rememberFormulaSelection(row, $event)"
                @blur="rememberFormulaSelection(row, $event)"
                @keyup.native="rememberFormulaSelection(row, $event)"
              />
              <div class="formula-toolbar">
                <el-select
                  :value="''"
                  size="mini"
                  filterable
                  placeholder="插入字段"
                  class="formula-field-select"
                  @change="fieldKey => insertFormulaField(row, fieldKey)"
                >
                  <el-option-group label="数据库字段">
                    <el-option
                      v-for="field in pageFields"
                      :key="field.fieldKey"
                      :label="field.formulaName || field.displayName"
                      :value="`field:${field.fieldKey}`"
                    />
                  </el-option-group>
                  <el-option-group v-if="formulaReferenceColumns(row).length" label="报表公式列">
                    <el-option
                      v-for="field in formulaReferenceColumns(row)"
                      :key="field.fieldKey"
                      :label="field.formulaName || field.displayName"
                      :value="`formula:${field.fieldKey}`"
                    />
                  </el-option-group>
                </el-select>
                <el-select
                  :value="''"
                  size="mini"
                  placeholder="运算 / 函数"
                  class="formula-token-select"
                  @change="command => insertFormulaShortcut(row, command)"
                >
                  <el-option-group label="运算符">
                    <el-option label="加  +" value="operator:+" />
                    <el-option label="减  -" value="operator:-" />
                    <el-option label="乘  ×" value="operator:*" />
                    <el-option label="除  ÷" value="operator:/" />
                    <el-option label="左括号  (" value="operator:(" />
                    <el-option label="右括号  )" value="operator:)" />
                  </el-option-group>
                  <el-option-group label="常用函数">
                    <el-option label="当前公式四舍五入" value="function:round" />
                    <el-option label="当前公式空值转 0" value="function:coalesce" />
                    <el-option label="当前公式零值转空" value="function:nullif" />
                  </el-option-group>
                </el-select>
                <el-input
                  v-model.trim="row.constantValue"
                  size="mini"
                  class="formula-constant-input"
                  placeholder="常量，如 0.05"
                  @keyup.enter.native="insertFormulaConstant(row)"
                >
                  <el-button slot="append" @click="insertFormulaConstant(row)">插入</el-button>
                </el-input>
                <el-tooltip content="清空公式" placement="top">
                  <el-button size="mini" icon="el-icon-delete" class="formula-clear-button" @click="clearFormula(row)" />
                </el-tooltip>
              </div>
              <div class="formula-help">可使用数字常量和括号；括号优先，其次乘除，最后加减。例如：([总收入] - [总支出]) * 0.95</div>
            </div>
            <el-input v-else size="mini" disabled placeholder="字段列无需公式" />
          </template>
        </el-table-column>
        <el-table-column label="聚合" width="120">
          <template slot-scope="{ row }">
            <el-select v-model="row.aggregateType" size="mini" style="width: 100%" :disabled="row.columnMode === 'formula'">
              <el-option label="分组" value="group" />
              <el-option label="求和" value="sum" />
              <el-option label="平均" value="avg" />
              <el-option label="最小" value="min" />
              <el-option label="最大" value="max" />
              <el-option label="计数" value="count" />
              <el-option label="去重计数" value="count_distinct" />
              <el-option label="公式" value="formula" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="格式" width="110">
          <template slot-scope="{ row }">
            <el-select v-model="row.numberFormat" size="mini" style="width: 100%">
              <el-option label="文本" value="text" />
              <el-option label="金额" value="amount" />
              <el-option label="整数" value="integer" />
              <el-option label="小数" value="decimal" />
              <el-option label="百分比" value="percent" />
              <el-option label="日期" value="date" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="列宽" width="90">
          <template slot-scope="{ row }">
            <el-input-number v-model="row.width" size="mini" :min="80" :max="360" :controls="false" style="width: 100%" />
          </template>
        </el-table-column>
        <el-table-column label="显示" width="80">
          <template slot-scope="{ row }">
            <el-switch v-model="row.visible" />
          </template>
        </el-table-column>
        <el-table-column label="排序" width="80">
          <template slot-scope="{ row }">
            <el-input-number v-model="row.sortOrder" size="mini" :min="0" :controls="false" style="width: 100%" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70" fixed="right">
          <template slot-scope="{ $index }">
            <el-button type="text" size="mini" class="danger-link" @click="removePageColumn($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div slot="footer" class="page-dialog-footer">
        <div>
          <el-button
            v-if="pageForm.id"
            size="small"
            icon="el-icon-document-copy"
            :loading="pageCopying"
            @click="copyCurrentReportPage"
          >复制报表</el-button>
          <el-button
            v-if="pageForm.id"
            type="danger"
            plain
            size="small"
            icon="el-icon-delete"
            :loading="pageDeleting"
            @click="deleteCurrentReportPage"
          >删除报表</el-button>
        </div>
        <div>
          <el-button size="small" @click="pageDialogVisible = false">取消</el-button>
          <el-button type="primary" size="small" :loading="pageSaving" @click="saveReportPage">保存</el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :title="editingConfig ? '编辑游戏配置' : '新增游戏配置'"
      :visible.sync="configDialogVisible"
      width="520px"
      append-to-body
    >
      <el-form :model="configForm" label-width="90px" size="small">
        <el-form-item label="游戏名称">
          <el-input v-model.trim="configForm.gameName" placeholder="请输入游戏名称" />
        </el-form-item>
        <el-form-item label="平台">
          <el-input v-model.trim="configForm.platform" placeholder="例如：抖音、微信" />
        </el-form-item>
        <el-form-item label="包名">
          <el-input v-model.trim="configForm.packageName" placeholder="请输入包名" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="configForm.enabled" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="configForm.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="configDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="configSaving" @click="saveConfig">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ProjectSummaryHistory',
  data() {
    const today = this.formatDate(new Date())
    const monthStart = today.slice(0, 8) + '01'
    let storedState = {}
    try {
      storedState = JSON.parse(localStorage.getItem('projectSummaryHistoryState') || '{}') || {}
    } catch (e) {
      storedState = {}
    }
    const storedReportQuery = storedState.reportQuery || {}
    const storedReportPage = storedState.reportPage || {}
    const storedConfigQuery = storedState.configQuery || {}
    const storedConfigPage = storedState.configPage || {}
    const storedGranularity = localStorage.getItem('projectSummaryDateGranularity')
    const storedReportGranularity = storedReportQuery.dateGranularity || storedGranularity
    const dateGranularity = ['day', 'month', 'year'].indexOf(storedReportGranularity) >= 0
      ? storedReportGranularity
      : 'day'
    const storedDateRange = Array.isArray(storedReportQuery.dateRange) && storedReportQuery.dateRange.length === 2
      ? storedReportQuery.dateRange
      : [monthStart, today]
    return {
      activeTab: storedState.activeTab || 'packageSummary',
      loading: false,
      datasets: [],
      datasetConnections: [
        { code: 'main', name: '业务数据库' },
        { code: 'overview', name: '项目概览数据库' },
        { code: 'jsd', name: '结算单数据库' }
      ],
      reportPages: [],
      schemas: {},
      reportRows: [],
      reportSummary: null,
      reportColumns: [],
      reportLoading: false,
      reportExporting: false,
      manualImporting: false,
      manualTemplateDownloading: false,
      syncingReportSort: false,
      reportQuery: {
        dateRange: storedDateRange,
        dateGranularity,
        gameName: storedReportQuery.gameName || '',
        platform: storedReportQuery.platform || '',
        packageName: storedReportQuery.packageName || '',
        keyword: storedReportQuery.keyword || '',
        sortField: storedReportQuery.sortField || '',
        sortDir: storedReportQuery.sortDir || ''
      },
      reportPage: {
        page: Math.max(1, Number(storedReportPage.page || 1)),
        pageSize: Number(storedReportPage.pageSize || localStorage.getItem('projectSummaryReportPageSize') || 50),
        total: 0
      },
      datasetDialogVisible: false,
      datasetSaving: false,
      datasetFieldSequence: 0,
      datasetForm: {
        id: null,
        code: '',
        name: '',
        connectionCode: 'main',
        sourceTable: '',
        timeGranularity: 'day',
        monthDateMode: 'single',
        dateColumn: '',
        periodStartColumn: '',
        periodEndColumn: '',
        periodAssignment: 'end_month',
        gameColumn: '',
        gameStorageMode: 'separate',
        platformColumn: '',
        defaultPlatform: '微信',
        packageColumn: '',
        suffixRules: [],
        fields: []
      },
      pageDialogVisible: false,
      pageSaving: false,
      pageCopying: false,
      pageDeleting: false,
      pageForm: {
        id: null,
        code: '',
        pageName: '',
        datasetId: null,
        datasetIds: [],
        defaultGroupBy: [],
        defaultSortField: '',
        defaultSortDir: 'desc',
        enabled: true,
        sortOrder: 0
      },
      pageFields: [],
      pageColumns: [],
      deletedColumnIds: [],
      formulaEditorSequence: 0,

      configRows: [],
      configLoading: false,
      configQuery: {
        keyword: storedConfigQuery.keyword || ''
      },
      configPage: {
        page: Math.max(1, Number(storedConfigPage.page || 1)),
        pageSize: Number(storedConfigPage.pageSize || localStorage.getItem('projectSummaryConfigPageSize') || 50),
        total: 0
      },
      configDialogVisible: false,
      editingConfig: null,
      configSaving: false,
      configForm: {
        gameName: '',
        platform: '抖音',
        packageName: '',
        remark: '',
        enabled: true
      }
    }
  },
  computed: {
    isReportTab() {
      return this.activeTab !== 'configs'
    },
    isManualAmountTab() {
      return this.activeTab === 'manualAmount'
    },
    activeReportPage() {
      return this.reportPages.find(item => item.code === this.activeTab) || null
    },
    activeSchema() {
      return this.schemas[this.activeTab] || {}
    },
    activeDatasetName() {
      const schema = this.activeSchema
      if (schema.datasets && schema.datasets.length) {
        return schema.datasets.map(dataset => dataset.name).join(' + ')
      }
      return schema.dataset ? schema.dataset.name : '-'
    },
    activeRequiresMonthly() {
      return !!(this.activeSchema && this.activeSchema.requiresMonthly)
    },
    hasDateGroup() {
      const page = (this.activeSchema && this.activeSchema.page) || this.activeReportPage || {}
      const configured = page.defaultGroupBy
      const groups = Array.isArray(configured)
        ? configured
        : String(configured || '').split(',').map(item => item.trim()).filter(Boolean)
      return this.hasField('date') && groups.indexOf('date') >= 0
    },
    reportDisplayRows() {
      const rows = []
      if (this.reportSummary) {
        rows.push(this.reportSummary)
      }
      return rows.concat(this.reportRows || [])
    },
    tableHeight() {
      return Math.max(420, window.innerHeight - 330)
    },
    pageGroupFields() {
      return (this.pageFields || []).filter(field => Number(field.groupable) === 1)
    },
    hasUnconfirmedNewDatasetFields() {
      return (this.datasetForm.fields || []).some(field => field.isNew && field.validationStatus !== 'exists')
    }
  },
  async mounted() {
    await this.loadInitial()
  },
  methods: {
    async loadInitial() {
      this.loading = true
      try {
        await this.loadDatasets()
        await this.loadReportPages()
        if (!this.reportPages.some(item => item.code === this.activeTab)) {
          this.activeTab = this.reportPages.length ? this.reportPages[0].code : 'configs'
        }
        if (this.isReportTab) {
          await this.loadReportData()
        } else {
          await this.loadConfigs()
        }
      } finally {
        this.loading = false
      }
    },
    async loadDatasets() {
      const response = await this.$axios.get('/project-summary/datasets')
      const data = this.readResponse(response, '查询数据源失败')
      this.datasets = data.rows || []
      if (Array.isArray(data.connections) && data.connections.length) {
        this.datasetConnections = data.connections
      }
    },
    openDatasetDialog() {
      if (!this.datasets.length) {
        this.openNewDatasetForm()
        this.datasetDialogVisible = true
        return
      }
      const schemaDataset = this.activeSchema && this.activeSchema.dataset
      const activeDatasetId = schemaDataset && schemaDataset.id
        ? Number(schemaDataset.id)
        : (this.activeReportPage ? Number(this.activeReportPage.datasetId) : Number(this.datasets[0].id))
      this.setDatasetForm(activeDatasetId)
      this.datasetDialogVisible = true
    },
    openNewDatasetForm() {
      this.datasetForm = this.emptyDatasetForm()
      this.datasetDialogVisible = true
    },
    handleDictionaryDatasetChange(datasetId) {
      this.setDatasetForm(datasetId)
    },
    setDatasetForm(datasetId) {
      const dataset = this.datasets.find(item => Number(item.id) === Number(datasetId)) || this.datasets[0] || {}
      this.datasetForm = {
        id: dataset.id ? Number(dataset.id) : null,
        code: dataset.code || '',
        name: dataset.name || '',
        connectionCode: dataset.connectionCode || 'main',
        sourceTable: dataset.sourceTable || '',
        timeGranularity: dataset.timeGranularity || 'day',
        monthDateMode: dataset.periodStartColumn && dataset.periodEndColumn ? 'range' : 'single',
        dateColumn: dataset.dateColumn || '',
        periodStartColumn: dataset.periodStartColumn || '',
        periodEndColumn: dataset.periodEndColumn || '',
        periodAssignment: dataset.periodAssignment || 'end_month',
        gameColumn: dataset.gameColumn || '',
        gameStorageMode: dataset.gameStorageMode || 'separate',
        platformColumn: dataset.platformColumn || '',
        defaultPlatform: dataset.defaultPlatform || '微信',
        packageColumn: dataset.packageColumn || '',
        suffixRules: this.parseSuffixRules(dataset.platformSuffixRules),
        fields: (dataset.fields || []).map(field => ({
          id: Number(field.id),
          fieldKey: field.fieldKey || '',
          displayName: field.displayName || '',
          columnName: field.columnName || '',
          sourceFieldName: field.sourceFieldName || '',
          dataType: field.dataType || 'number',
          groupable: Number(field.groupable) === 1 || field.groupable === true,
          defaultAggregate: field.defaultAggregate || (Number(field.groupable) === 1 ? 'group' : 'sum'),
          isNew: false,
          validating: false,
          validationStatus: 'existing',
          validationMessage: '',
          validatedColumn: '',
          fieldKeyAuto: false
        }))
      }
    },
    emptyDatasetForm() {
      return {
        id: null,
        code: '',
        name: '',
        connectionCode: (this.datasetConnections[0] && this.datasetConnections[0].code) || 'main',
        sourceTable: '',
        timeGranularity: 'day',
        monthDateMode: 'single',
        dateColumn: '',
        periodStartColumn: '',
        periodEndColumn: '',
        periodAssignment: 'end_month',
        gameColumn: '',
        gameStorageMode: 'separate',
        platformColumn: '',
        defaultPlatform: '微信',
        packageColumn: '',
        suffixRules: [
          { suffix: '(抖音)', platform: '抖音' },
          { suffix: '（抖音）', platform: '抖音' },
          { suffix: '(微信)', platform: '微信' },
          { suffix: '（微信）', platform: '微信' },
          { suffix: '(视频号)', platform: '视频号' },
          { suffix: '（视频号）', platform: '视频号' }
        ],
        fields: []
      }
    },
    parseSuffixRules(raw) {
      if (!raw) return this.emptyDatasetForm().suffixRules
      try {
        const object = typeof raw === 'string' ? JSON.parse(raw) : raw
        return Object.keys(object || {}).map(suffix => ({ suffix, platform: object[suffix] }))
      } catch (e) {
        return this.emptyDatasetForm().suffixRules
      }
    },
    addSuffixRule() {
      this.datasetForm.suffixRules.push({ suffix: '', platform: '' })
    },
    removeSuffixRule(index) {
      this.datasetForm.suffixRules.splice(index, 1)
    },
    addDatasetField() {
      this.datasetForm.fields.push({
        id: null,
        clientId: `new_${Date.now()}_${++this.datasetFieldSequence}`,
        fieldKey: '',
        displayName: '',
        columnName: '',
        sourceFieldName: '',
        dataType: 'number',
        groupable: false,
        defaultAggregate: 'sum',
        isNew: true,
        validating: false,
        validationStatus: 'unchecked',
        validationMessage: '',
        validatedColumn: '',
        fieldKeyAuto: true
      })
      this.$nextTick(() => {
        this.$message.info('请填写数据库字段名并检查是否存在')
      })
    },
    removeDatasetField(index) {
      this.datasetForm.fields.splice(index, 1)
    },
    handleDatasetColumnInput(row) {
      row.validationStatus = 'unchecked'
      row.validationMessage = ''
      row.validatedColumn = ''
      if (row.fieldKeyAuto) {
        row.fieldKey = this.toInternalFieldKey(row.columnName)
      }
    },
    markDatasetFieldKeyManual(row) {
      row.fieldKeyAuto = false
    },
    toInternalFieldKey(columnName) {
      const parts = String(columnName || '').toLowerCase().split(/_+/).filter(Boolean)
      let value = parts.map((part, index) => index === 0
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1)).join('')
      if (/^\d/.test(value)) value = `field_${value}`
      return value
    },
    async validateDatasetField(row, force) {
      if (!row || !row.isNew || row.validating) return row && row.validationStatus === 'exists'
      const columnName = String(row.columnName || '').trim()
      if (!columnName) {
        row.validationStatus = 'unchecked'
        row.validationMessage = '请输入数据库字段名'
        return false
      }
      const normalizedColumnName = columnName.toLowerCase()
      const duplicate = (this.datasetForm.fields || []).find(field => field !== row &&
        String(field.columnName || '').trim().toLowerCase() === normalizedColumnName)
      if (duplicate) {
        row.validationStatus = 'duplicate'
        row.validationMessage = '该数据库字段已经配置'
        row.validatedColumn = ''
        if (force) this.$message.warning(`数据库字段 ${columnName} 已配置`)
        return false
      }
      if (!force && row.validatedColumn.toLowerCase() === columnName.toLowerCase() &&
        ['exists', 'missing'].indexOf(row.validationStatus) >= 0) {
        return row.validationStatus === 'exists'
      }
      row.validating = true
      row.validationStatus = 'checking'
      row.validationMessage = ''
      try {
        const response = await this.$axios.post(
          `/project-summary/datasets/${this.datasetForm.id}/fields/validate`,
          { columnName }
        )
        const data = this.readResponse(response, '检查数据库字段失败')
        row.validatedColumn = columnName
        row.validationMessage = data.message || ''
        if (!data.exists) {
          row.validationStatus = 'missing'
          this.$message.warning(`数据库字段 ${columnName} 不存在`)
          return false
        }
        row.columnName = data.columnName || columnName
        row.validatedColumn = row.columnName
        if (!row.fieldKey || row.fieldKeyAuto) {
          row.fieldKey = data.suggestedFieldKey || this.toInternalFieldKey(row.columnName)
          row.fieldKeyAuto = true
        }
        if (data.dataType) {
          row.dataType = data.dataType
          row.groupable = data.dataType !== 'number'
          row.defaultAggregate = row.groupable ? 'group' : 'sum'
        }
        row.validationStatus = 'exists'
        this.$message.success(`字段 ${row.columnName} 存在，可以保存`)
        return true
      } catch (error) {
        row.validationStatus = 'error'
        row.validationMessage = error && error.message ? error.message : '检查失败'
        return false
      } finally {
        row.validating = false
      }
    },
    syncDictionaryField(row) {
      row.defaultAggregate = row.groupable ? 'group' : (row.defaultAggregate === 'group' ? 'sum' : row.defaultAggregate || 'sum')
    },
    dictionaryColumnName(columnName) {
      return columnName === '__package_name' ? '关联游戏配置（包名）' : (columnName || '-')
    },
    async saveDatasetDictionary() {
      if (!this.datasetForm.name || !this.datasetForm.sourceTable || !this.datasetForm.connectionCode) {
        this.$message.warning('请填写表中文名、数据库连接和数据库表名')
        return
      }
      if (!this.datasetForm.id && (!this.datasetForm.code || !/^[A-Za-z0-9_]+$/.test(this.datasetForm.code))) {
        this.$message.warning('数据表编码只能包含英文字母、数字和下划线')
        return
      }
      if (this.datasetForm.timeGranularity === 'month' && this.datasetForm.monthDateMode === 'range') {
        if (!this.datasetForm.periodStartColumn || !this.datasetForm.periodEndColumn) {
          this.$message.warning('请填写周期开始字段和周期结束字段')
          return
        }
      } else if (!this.datasetForm.dateColumn) {
        this.$message.warning(this.datasetForm.timeGranularity === 'day' ? '请填写日期字段' : '请填写月份字段')
        return
      }
      const names = new Set()
      const fieldKeys = new Set()
      const columnNames = new Set((this.datasetForm.fields || [])
        .filter(field => !field.isNew && field.columnName)
        .map(field => String(field.columnName).trim().toLowerCase()))
      for (const field of this.datasetForm.fields) {
        const displayName = String(field.displayName || '').trim()
        if (!displayName) {
          this.$message.warning('字段中文名不能为空')
          return
        }
        const uniqueName = displayName.toLowerCase()
        if (names.has(uniqueName)) {
          this.$message.warning(`字段中文名不能重复：${displayName}`)
          return
        }
        names.add(uniqueName)
        const fieldKey = String(field.fieldKey || '').trim()
        if (!fieldKey || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(fieldKey)) {
          this.$message.warning(`内部字段需以英文字母或下划线开头，且只能包含英文字母、数字和下划线：${fieldKey || '空'}`)
          return
        }
        const normalizedFieldKey = fieldKey.toLowerCase()
        if (fieldKeys.has(normalizedFieldKey)) {
          this.$message.warning(`内部字段不能重复：${fieldKey}`)
          return
        }
        fieldKeys.add(normalizedFieldKey)
        if (field.isNew) {
          const columnName = String(field.columnName || '').trim()
          if (field.validationStatus !== 'exists') {
            this.$message.warning(`请先检查数据库字段：${columnName || '未填写'}`)
            return
          }
          const normalizedColumnName = columnName.toLowerCase()
          if (columnNames.has(normalizedColumnName)) {
            this.$message.warning(`数据库字段不能重复：${columnName}`)
            return
          }
          columnNames.add(normalizedColumnName)
        }
      }
      this.datasetSaving = true
      try {
        const payload = Object.assign(this.datasetMetadataPayload(), {
          fields: this.datasetForm.fields.map(field => ({
            id: field.id,
            fieldKey: field.fieldKey,
            columnName: field.columnName,
            displayName: field.displayName,
            dataType: field.dataType,
            groupable: !!field.groupable,
            defaultAggregate: field.defaultAggregate
          }))
        })
        if (!this.datasetForm.id) {
          const response = await this.$axios.post('/project-summary/datasets', payload)
          const data = this.readResponse(response, '创建数据表失败') || {}
          await this.loadDatasets()
          this.setDatasetForm(Number(data.id))
          this.$message.success('数据表已创建，请继续新增并验证需要的字段')
          return
        }
        const response = await this.$axios.put(`/project-summary/datasets/${this.datasetForm.id}`, payload)
        this.readResponse(response, '保存数据字典失败')
        this.$message.success('保存成功')
        this.datasetDialogVisible = false
        this.schemas = {}
        await this.loadDatasets()
        await this.loadReportPages()
        if (this.isReportTab) {
          await this.loadReportData()
        }
      } finally {
        this.datasetSaving = false
      }
    },
    datasetMetadataPayload() {
      const rangeMode = this.datasetForm.timeGranularity === 'month' && this.datasetForm.monthDateMode === 'range'
      const suffixRuleObject = {}
      ;(this.datasetForm.suffixRules || []).forEach(rule => {
        const suffix = String(rule.suffix || '').trim()
        const platform = String(rule.platform || '').trim()
        if (suffix && platform) suffixRuleObject[suffix] = platform
      })
      return {
        code: this.datasetForm.code,
        name: this.datasetForm.name,
        connectionCode: this.datasetForm.connectionCode,
        sourceTable: this.datasetForm.sourceTable,
        timeGranularity: this.datasetForm.timeGranularity,
        dateColumn: rangeMode ? '' : this.datasetForm.dateColumn,
        periodStartColumn: rangeMode ? this.datasetForm.periodStartColumn : '',
        periodEndColumn: rangeMode ? this.datasetForm.periodEndColumn : '',
        periodAssignment: rangeMode ? this.datasetForm.periodAssignment : 'end_month',
        gameColumn: this.datasetForm.gameColumn,
        gameStorageMode: this.datasetForm.gameStorageMode,
        platformColumn: this.datasetForm.gameStorageMode === 'separate' ? this.datasetForm.platformColumn : '',
        defaultPlatform: this.datasetForm.gameStorageMode === 'suffix' ? this.datasetForm.defaultPlatform : '',
        platformSuffixRules: JSON.stringify(suffixRuleObject),
        packageColumn: this.datasetForm.packageColumn
      }
    },
    async loadReportPages() {
      const response = await this.$axios.get('/project-summary/report-pages')
      const data = this.readResponse(response, '查询报表页失败')
      this.reportPages = data.rows || []
    },
    async loadReportSchema(code) {
      const pageCode = code || this.activeTab
      if (!pageCode || pageCode === 'configs') return null
      if (this.schemas[pageCode]) {
        return this.schemas[pageCode]
      }
      const response = await this.$axios.get(`/project-summary/report-pages/${pageCode}/schema`)
      const data = this.readResponse(response, '查询报表结构失败')
      this.$set(this.schemas, pageCode, data)
      if (data.requiresMonthly && this.reportQuery.dateGranularity === 'day') {
        this.reportQuery.dateGranularity = 'month'
      }
      return data
    },
    async loadReportData() {
      if (!this.isReportTab) return
      this.reportLoading = true
      try {
        await this.loadReportSchema(this.activeTab)
        const response = await this.$axios.get(`/project-summary/report-pages/${this.activeTab}/data`, {
          params: this.reportRequestParams(true)
        })
        const data = this.readResponse(response, '查询报表数据失败')
        this.reportRows = data.rows || []
        this.reportSummary = data.summary || null
        this.reportColumns = data.columns || []
        this.reportPage.total = Number(data.total || 0)
        this.reportPage.page = Number(data.page || this.reportPage.page)
        this.reportPage.pageSize = Number(data.pageSize || this.reportPage.pageSize)
        this.reportQuery.sortField = data.sortField || ''
        this.reportQuery.sortDir = data.sortDir || ''
        this.saveProjectSummaryState()
        this.syncReportTableSort()
      } finally {
        this.reportLoading = false
      }
    },
    reportRequestParams(includePagination) {
      const range = Array.isArray(this.reportQuery.dateRange) ? this.reportQuery.dateRange : []
      const params = {
        startDate: range[0] || '',
        endDate: range[1] || '',
        dateGranularity: this.activeRequiresMonthly && this.reportQuery.dateGranularity === 'day'
          ? 'month'
          : this.reportQuery.dateGranularity,
        gameName: this.reportQuery.gameName,
        platform: this.reportQuery.platform,
        packageName: this.reportQuery.packageName,
        keyword: this.reportQuery.keyword,
        sortField: this.reportQuery.sortField,
        sortDir: this.reportQuery.sortDir
      }
      if (includePagination) {
        params.page = this.reportPage.page
        params.pageSize = this.reportPage.pageSize
      }
      return params
    },
    async exportReport() {
      if (!this.isReportTab || this.reportPage.total === 0) return
      this.reportExporting = true
      try {
        const response = await this.$axios.get(`/project-summary/report-pages/${this.activeTab}/export`, {
          params: this.reportRequestParams(false),
          responseType: 'blob',
          timeout: 180000
        })
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        link.download = this.exportDownloadFileName(response)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$message.success('导出完成')
      } catch (error) {
        const message = error && error.message ? error.message : '导出报表失败'
        this.$message.error(message)
      } finally {
        this.reportExporting = false
      }
    },
    async importManualAmount({ file }) {
      if (!file || this.manualImporting) return
      this.manualImporting = true
      try {
        const form = new FormData()
        form.append('file', file)
        const response = await this.$axios.post('/project-summary/manual-amount/import', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000
        })
        const data = this.readResponse(response, '导入手动数据失败')
        const imported = Number(data.importedRows || 0)
        const skipped = Number(data.skippedRows || 0)
        const errors = Array.isArray(data.errors) ? data.errors : []
        this.$message.success(`导入完成：导入/更新 ${imported} 条，跳过 ${skipped} 条`)
        if (errors.length) {
          this.$alert(errors.join('\n'), '部分行未导入', {
            confirmButtonText: '知道了',
            customClass: 'manual-import-error-dialog'
          })
        }
        delete this.schemas[this.activeTab]
        await this.loadDatasets()
        await this.loadReportSchema(this.activeTab)
        await this.loadReportData()
      } catch (error) {
        const message = error && error.message ? error.message : '导入手动数据失败'
        this.$message.error(message)
      } finally {
        this.manualImporting = false
      }
    },
    async downloadManualTemplate() {
      if (this.manualTemplateDownloading) return
      this.manualTemplateDownloading = true
      try {
        const response = await this.$axios.get('/project-summary/manual-amount/template', {
          responseType: 'blob',
          timeout: 60000
        })
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        link.download = this.exportDownloadFileName(response) || '手动导入数据模板.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        const message = error && error.message ? error.message : '下载模板失败'
        this.$message.error(message)
      } finally {
        this.manualTemplateDownloading = false
      }
    },
    exportDownloadFileName(response) {
      const headers = response && response.headers ? response.headers : {}
      const disposition = headers['content-disposition'] || headers['Content-Disposition'] || ''
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1].replace(/^"|"$/g, ''))
        } catch (e) {
          // Fall through to the readable local fallback name.
        }
      }
      const pageName = this.activeReportPage ? this.activeReportPage.pageName : '项目汇总'
      const range = Array.isArray(this.reportQuery.dateRange) ? this.reportQuery.dateRange : []
      const dateLabel = range[0] && range[1]
        ? (range[0] === range[1] ? `_${range[0]}` : `_${range[0]}至${range[1]}`)
        : ''
      return `${pageName || '项目汇总'}${dateLabel}.xlsx`
    },
    async loadConfigs() {
      this.configLoading = true
      try {
        const response = await this.$axios.get('/project-summary/configs', {
          params: {
            keyword: this.configQuery.keyword,
            page: this.configPage.page,
            pageSize: this.configPage.pageSize
          }
        })
        const data = this.readResponse(response, '查询游戏配置失败')
        this.configRows = data.rows || []
        this.configPage.total = Number(data.total || 0)
        this.configPage.page = Number(data.page || this.configPage.page)
        this.configPage.pageSize = Number(data.pageSize || this.configPage.pageSize)
        this.saveProjectSummaryState()
      } finally {
        this.configLoading = false
      }
    },
    async handleTabChange() {
      if (this.isReportTab) {
        this.reportPage.page = 1
        await this.loadReportData()
      } else {
        this.configPage.page = 1
        await this.loadConfigs()
      }
    },
    async refreshCurrent() {
      if (this.isReportTab) {
        delete this.schemas[this.activeTab]
        await this.loadReportData()
      } else {
        await this.loadConfigs()
      }
    },
    searchReport() {
      this.reportPage.page = 1
      this.loadReportData()
    },
    resetReport() {
      const today = this.formatDate(new Date())
      this.reportQuery = {
        dateRange: [today.slice(0, 8) + '01', today],
        dateGranularity: this.reportQuery.dateGranularity || 'day',
        gameName: '',
        platform: '',
        packageName: '',
        keyword: '',
        sortField: '',
        sortDir: ''
      }
      this.searchReport()
    },
    handleDateGranularityChange(value) {
      localStorage.setItem('projectSummaryDateGranularity', value || 'day')
      this.reportPage.page = 1
      this.loadReportData()
    },
    handleReportSizeChange(size) {
      this.reportPage.pageSize = size
      this.reportPage.page = 1
      localStorage.setItem('projectSummaryReportPageSize', String(size))
      this.loadReportData()
    },
    handleReportCurrentChange(page) {
      this.reportPage.page = page
      this.loadReportData()
    },
    handleReportSortChange({ prop, order }) {
      if (this.syncingReportSort) return
      this.reportQuery.sortField = prop || ''
      this.reportQuery.sortDir = order === 'descending' ? 'desc' : (order === 'ascending' ? 'asc' : '')
      this.reportPage.page = 1
      this.loadReportData()
    },
    saveProjectSummaryState() {
      try {
        const reportRange = Array.isArray(this.reportQuery.dateRange)
          ? this.reportQuery.dateRange.slice(0, 2)
          : []
        const state = {
          activeTab: this.activeTab,
          reportQuery: {
            dateRange: reportRange,
            dateGranularity: this.reportQuery.dateGranularity || 'day',
            gameName: this.reportQuery.gameName || '',
            platform: this.reportQuery.platform || '',
            packageName: this.reportQuery.packageName || '',
            keyword: this.reportQuery.keyword || '',
            sortField: this.reportQuery.sortField || '',
            sortDir: this.reportQuery.sortDir || ''
          },
          reportPage: {
            page: this.reportPage.page || 1,
            pageSize: this.reportPage.pageSize || 50
          },
          configQuery: {
            keyword: this.configQuery.keyword || ''
          },
          configPage: {
            page: this.configPage.page || 1,
            pageSize: this.configPage.pageSize || 50
          }
        }
        localStorage.setItem('projectSummaryHistoryState', JSON.stringify(state))
        localStorage.setItem('projectSummaryDateGranularity', state.reportQuery.dateGranularity)
        localStorage.setItem('projectSummaryReportPageSize', String(state.reportPage.pageSize))
        localStorage.setItem('projectSummaryConfigPageSize', String(state.configPage.pageSize))
      } catch (e) {
        // Ignore local storage failures, such as private browsing restrictions.
      }
    },
    syncReportTableSort() {
      this.$nextTick(() => {
        const table = this.$refs.reportTable
        if (!table) return
        this.syncingReportSort = true
        table.clearSort()
        if (this.reportQuery.sortField && this.reportQuery.sortDir) {
          table.sort(
            this.reportQuery.sortField,
            this.reportQuery.sortDir === 'desc' ? 'descending' : 'ascending'
          )
        }
        this.$nextTick(() => {
          this.syncingReportSort = false
        })
      })
    },
    searchConfigs() {
      this.configPage.page = 1
      this.loadConfigs()
    },
    resetConfigs() {
      this.configQuery.keyword = ''
      this.searchConfigs()
    },
    handleConfigSizeChange(size) {
      this.configPage.pageSize = size
      this.configPage.page = 1
      localStorage.setItem('projectSummaryConfigPageSize', String(size))
      this.loadConfigs()
    },
    handleConfigCurrentChange(page) {
      this.configPage.page = page
      this.loadConfigs()
    },
    hasField(fieldKey) {
      const schema = this.activeSchema
      const fields = schema.fields || []
      return fields.some(field => field.fieldKey === fieldKey)
    },
    async openReportPageDialog() {
      if (!this.isReportTab || !this.activeReportPage) return
      const schema = await this.loadReportSchema(this.activeTab)
      this.pageForm = Object.assign(this.emptyPageForm(), {
        id: Number(schema.page.id),
        code: schema.page.code,
        pageName: schema.page.pageName,
        datasetId: Number(schema.page.datasetId),
        datasetIds: (schema.datasets || []).map(dataset => Number(dataset.id)),
        defaultGroupBy: Array.isArray(schema.page.defaultGroupBy) ? schema.page.defaultGroupBy.slice() : [],
        defaultSortField: schema.page.defaultSortField || '',
        defaultSortDir: schema.page.defaultSortDir || 'asc',
        enabled: Number(schema.page.enabled) === 1,
        sortOrder: Number(schema.page.sortOrder || 0)
      })
      this.pageFields = schema.fields || []
      this.pageColumns = (schema.columns || []).map(item => this.normalizeColumnForm(item))
      this.deletedColumnIds = []
      this.pageDialogVisible = true
    },
    openNewReportDialog() {
      const dataset = this.datasets[0] || {}
      this.pageForm = Object.assign(this.emptyPageForm(), {
        datasetId: dataset.id ? Number(dataset.id) : null,
        datasetIds: dataset.id ? [Number(dataset.id)] : []
      })
      this.pageFields = this.buildPageFields(this.pageForm.datasetIds)
      this.pageColumns = []
      this.deletedColumnIds = []
      this.pageDialogVisible = true
    },
    handleDatasetChange(datasetIds) {
      const ids = (datasetIds || []).map(id => Number(id))
      this.pageForm.datasetId = ids.length ? ids[0] : null
      this.pageFields = this.buildPageFields(ids)
      this.pageForm.defaultGroupBy = (this.pageForm.defaultGroupBy || [])
        .filter(key => this.pageFields.some(field => field.fieldKey === key))
      if (!this.pageFields.some(field => field.fieldKey === this.pageForm.defaultSortField)) {
        this.pageForm.defaultSortField = ''
      }
    },
    buildPageFields(datasetIds) {
      const result = []
      const canonicalDimensions = ['date', 'gameName', 'platform', 'packageName']
      const addedDimensions = new Set()
      ;(datasetIds || []).forEach((datasetId, datasetIndex) => {
        const dataset = this.datasets.find(item => Number(item.id) === Number(datasetId))
        if (!dataset) return
        ;(dataset.fields || []).forEach(original => {
          const nativeKey = original.fieldKey
          const isCanonical = canonicalDimensions.indexOf(nativeKey) >= 0 && Number(original.groupable) === 1
          if (isCanonical && addedDimensions.has(nativeKey)) return
          if (isCanonical) addedDimensions.add(nativeKey)
          const primary = datasetIndex === 0
          result.push(Object.assign({}, original, {
            datasetId: Number(dataset.id),
            nativeFieldKey: nativeKey,
            fieldKey: primary || isCanonical ? nativeKey : `d${dataset.id}__${nativeKey}`,
            formulaName: primary || isCanonical ? original.displayName : `${dataset.name}.${original.displayName}`,
            primary
          }))
        })
      })
      return result
    },
    addPageColumn() {
      this.pageColumns.push({
        id: null,
        displayName: '',
        columnMode: 'field',
        fieldKey: '',
        formula: '',
        aggregateType: 'sum',
        numberFormat: 'amount',
        width: 120,
        visible: true,
        sortOrder: (this.pageColumns.length + 1) * 10,
        isDimension: false,
        constantValue: '',
        editorKey: `new_${++this.formulaEditorSequence}`,
        formulaSelectionStart: null,
        formulaSelectionEnd: null
      })
    },
    removePageColumn(index) {
      const row = this.pageColumns[index]
      if (row && row.id) {
        this.deletedColumnIds.push(row.id)
      }
      this.pageColumns.splice(index, 1)
    },
    syncColumnByField(row) {
      if (row.columnMode === 'formula') return
      const field = this.pageFields.find(item => item.fieldKey === row.fieldKey)
      if (!field) return
      row.formula = ''
      if (!row.displayName) row.displayName = field.displayName
      const isDimension = Number(field.groupable) === 1
      row.isDimension = isDimension
      row.aggregateType = isDimension ? 'group' : (field.defaultAggregate || 'sum')
      row.numberFormat = field.dataType === 'date' ? 'date' : (isDimension ? 'text' : row.numberFormat || 'amount')
    },
    changeColumnMode(row) {
      if (row.columnMode === 'formula') {
        row.fieldKey = ''
        row.aggregateType = 'formula'
        row.isDimension = false
        if (row.numberFormat === 'date' || row.numberFormat === 'text') {
          row.numberFormat = 'amount'
        }
        return
      }
      row.formula = ''
      row.aggregateType = 'sum'
      row.isDimension = false
      if (row.fieldKey) this.syncColumnByField(row)
    },
    syncFormulaColumn(row) {
      if (row.columnMode === 'formula') {
        row.fieldKey = ''
        row.aggregateType = 'formula'
        row.isDimension = false
      }
    },
    rememberFormulaSelection(row, event) {
      const target = event && event.target
      if (!target || typeof target.selectionStart !== 'number') return
      row.formulaSelectionStart = target.selectionStart
      row.formulaSelectionEnd = target.selectionEnd
    },
    replaceFormulaSelection(row, text) {
      const formula = row.formula || ''
      const hasCursor = Number.isInteger(row.formulaSelectionStart)
      const start = hasCursor ? Math.min(row.formulaSelectionStart, formula.length) : formula.length
      const end = Number.isInteger(row.formulaSelectionEnd)
        ? Math.max(start, Math.min(row.formulaSelectionEnd, formula.length))
        : start
      row.formula = `${formula.slice(0, start)}${text}${formula.slice(end)}`
      row.formulaSelectionStart = start + text.length
      row.formulaSelectionEnd = row.formulaSelectionStart
      this.syncFormulaColumn(row)
      this.focusFormulaInput(row)
    },
    focusFormulaInput(row) {
      this.$nextTick(() => {
        let input = this.$refs[`formulaInput_${row.editorKey}`]
        if (Array.isArray(input)) input = input[0]
        const element = input && input.$refs ? input.$refs.input : null
        if (!element) return
        element.focus()
        const cursor = Number.isInteger(row.formulaSelectionStart) ? row.formulaSelectionStart : (row.formula || '').length
        element.setSelectionRange(cursor, cursor)
      })
    },
    formulaReferenceColumns(row) {
      return this.pageColumns
        .filter(item => item !== row && item.id && item.columnMode === 'formula' && item.displayName && item.formula)
        .map(item => ({
          fieldKey: `formula_${item.id}`,
          displayName: item.displayName,
          formulaName: item.displayName
        }))
    },
    insertFormulaField(row, fieldKey) {
      const value = String(fieldKey || '')
      const parts = value.indexOf(':') >= 0 ? value.split(':') : ['field', value]
      const source = parts[0]
      const key = parts.slice(1).join(':')
      const field = source === 'formula'
        ? this.formulaReferenceColumns(row).find(item => item.fieldKey === key)
        : this.pageFields.find(item => item.fieldKey === key)
      if (!field || row.columnMode !== 'formula') return
      this.replaceFormulaSelection(row, `[${field.formulaName || field.displayName}]`)
    },
    insertFormulaConstant(row) {
      if (!row || row.columnMode !== 'formula') return
      const value = String(row.constantValue || '').trim()
      if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value)) {
        this.$message.warning('常量请输入数字，例如 0.05 或 100')
        return
      }
      this.replaceFormulaSelection(row, value)
      row.constantValue = ''
    },
    insertFormulaShortcut(row, command) {
      if (!command || row.columnMode !== 'formula') return
      const parts = command.split(':')
      if (parts[0] === 'operator') {
        const token = parts[1]
        this.replaceFormulaSelection(row, ['+', '-', '*', '/'].indexOf(token) >= 0 ? ` ${token} ` : token)
        return
      }
      const formula = (row.formula || '').trim()
      if (!formula) {
        this.$message.warning('请先插入字段或填写公式')
        return
      }
      const functionName = parts[1]
      if (functionName === 'round') row.formula = `round(${formula}, 2)`
      if (functionName === 'coalesce') row.formula = `coalesce(${formula}, 0)`
      if (functionName === 'nullif') row.formula = `nullif(${formula}, 0)`
      row.formulaSelectionStart = row.formula.length
      row.formulaSelectionEnd = row.formula.length
      this.syncFormulaColumn(row)
      this.focusFormulaInput(row)
    },
    clearFormula(row) {
      row.formula = ''
      row.formulaSelectionStart = 0
      row.formulaSelectionEnd = 0
      this.syncFormulaColumn(row)
      this.focusFormulaInput(row)
    },
    async copyCurrentReportPage() {
      const page = this.pageForm.id
        ? { id: this.pageForm.id, code: this.pageForm.code, pageName: this.pageForm.pageName }
        : this.activeReportPage
      if (!page || !page.id) return
      const action = await this.$prompt('请输入新报表名称', '复制报表', {
        confirmButtonText: '复制',
        cancelButtonText: '取消',
        inputValue: `${page.pageName || '报表'} 副本`,
        inputPattern: /\S+/,
        inputErrorMessage: '请填写报表名称'
      }).catch(() => null)
      if (!action) return
      this.pageCopying = true
      try {
        const response = await this.$axios.post(`/project-summary/report-pages/${page.id}/copy`, {
          pageName: action.value
        })
        const copiedPage = this.readResponse(response, '复制报表失败') || {}
        this.$message.success('复制成功')
        this.pageDialogVisible = false
        this.schemas = {}
        await this.loadReportPages()
        if (copiedPage.code) {
          this.activeTab = copiedPage.code
        }
        if (this.isReportTab) {
          await this.loadReportData()
        }
      } finally {
        this.pageCopying = false
      }
    },
    async deleteCurrentReportPage() {
      const page = this.pageForm.id
        ? { id: this.pageForm.id, code: this.pageForm.code, pageName: this.pageForm.pageName }
        : this.activeReportPage
      if (!page || !page.id) return
      const confirmed = await this.$confirm(`确认删除报表“${page.pageName || page.code}”？列配置也会一起删除。`, '删除报表', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).catch(() => null)
      if (!confirmed) return
      this.pageDeleting = true
      try {
        const response = await this.$axios.delete(`/project-summary/report-pages/${page.id}`)
        this.readResponse(response, '删除报表失败')
        this.$message.success('删除成功')
        this.pageDialogVisible = false
        if (page.code) {
          this.$delete(this.schemas, page.code)
        } else {
          this.schemas = {}
        }
        await this.loadReportPages()
        if (!this.reportPages.some(item => item.code === this.activeTab)) {
          this.activeTab = this.reportPages.length ? this.reportPages[0].code : 'configs'
        }
        if (this.isReportTab) {
          await this.loadReportData()
        } else {
          await this.loadConfigs()
        }
      } finally {
        this.pageDeleting = false
      }
    },
    async saveReportPage() {
      if (!this.pageForm.pageName) {
        this.$message.warning('请填写页面名称')
        return
      }
      if (!this.pageForm.datasetId) {
        this.$message.warning('请选择数据源')
        return
      }
      const invalidColumnIndex = this.pageColumns.findIndex(column => {
        if (!column.displayName) return true
        return column.columnMode === 'formula' ? !column.formula : !column.fieldKey
      })
      if (invalidColumnIndex >= 0) {
        const column = this.pageColumns[invalidColumnIndex]
        const reason = !column.displayName
          ? '请填写显示名'
          : (column.columnMode === 'formula' ? '请填写公式' : '请选择字段')
        this.$message.warning(`第 ${invalidColumnIndex + 1} 列：${reason}`)
        return
      }
      this.pageSaving = true
      try {
        const payload = Object.assign({}, this.pageForm)
        payload.datasetIds = (this.pageForm.datasetIds || []).map(id => Number(id))
        payload.datasetId = payload.datasetIds.length ? payload.datasetIds[0] : null
        const response = this.pageForm.id
          ? await this.$axios.put(`/project-summary/report-pages/${this.pageForm.id}`, payload)
          : await this.$axios.post('/project-summary/report-pages', payload)
        const savedPage = this.readResponse(response, '保存报表页失败') || {}
        const pageId = this.pageForm.id || Number(savedPage.id || 0)
        if (pageId) {
          for (const columnId of this.deletedColumnIds) {
            await this.$axios.delete(`/project-summary/report-pages/${pageId}/columns/${columnId}`)
          }
          for (const column of this.pageColumns) {
            const columnPayload = Object.assign({}, column)
            const formulaColumn = column.columnMode === 'formula'
            columnPayload.fieldKey = formulaColumn ? '' : column.fieldKey
            columnPayload.formula = formulaColumn ? column.formula : ''
            columnPayload.aggregateType = formulaColumn ? 'formula' : column.aggregateType
            columnPayload.isDimension = formulaColumn ? false : !!column.isDimension
            columnPayload.visible = !!columnPayload.visible
            delete columnPayload.columnMode
            delete columnPayload.editorKey
            delete columnPayload.formulaSelectionStart
            delete columnPayload.formulaSelectionEnd
            delete columnPayload.constantValue
            if (column.id) {
              await this.$axios.put(`/project-summary/report-pages/${pageId}/columns/${column.id}`, columnPayload)
            } else {
              await this.$axios.post(`/project-summary/report-pages/${pageId}/columns`, columnPayload)
            }
          }
        }
        this.$message.success('保存成功')
        this.pageDialogVisible = false
        this.schemas = {}
        await this.loadReportPages()
        if (!this.reportPages.some(item => item.code === this.activeTab)) {
          this.activeTab = this.reportPages.length ? this.reportPages[0].code : 'configs'
        }
        if (this.isReportTab) {
          await this.loadReportData()
        }
      } finally {
        this.pageSaving = false
      }
    },
    openConfigDialog(row) {
      this.editingConfig = row || null
      this.configForm = row
        ? {
            gameName: row.gameName || '',
            platform: row.platform || '',
            packageName: row.packageName || '',
            remark: row.remark || '',
            enabled: Number(row.enabled) === 1
          }
        : this.emptyConfigForm()
      this.configDialogVisible = true
    },
    async saveConfig() {
      if (!this.configForm.gameName || !this.configForm.platform || !this.configForm.packageName) {
        this.$message.warning('请填写游戏名称、平台和包名')
        return
      }
      this.configSaving = true
      try {
        const payload = Object.assign({}, this.configForm)
        const response = this.editingConfig
          ? await this.$axios.put(`/project-summary/configs/${this.editingConfig.id}`, payload)
          : await this.$axios.post('/project-summary/configs', payload)
        this.readResponse(response, '保存游戏配置失败')
        this.$message.success('保存成功')
        this.configDialogVisible = false
        await this.loadConfigs()
      } finally {
        this.configSaving = false
      }
    },
    async deleteConfig(row) {
      try {
        await this.$confirm(`确认删除 ${row.gameName} / ${row.packageName}？`, '提示', { type: 'warning' })
      } catch (e) {
        return
      }
      const response = await this.$axios.delete(`/project-summary/configs/${row.id}`)
      this.readResponse(response, '删除游戏配置失败')
      this.$message.success('删除成功')
      await this.loadConfigs()
    },
    normalizeColumnForm(item) {
      const fieldKey = item.fieldKey || ''
      const formula = item.displayFormula || item.formula || ''
      return {
        id: item.id ? Number(item.id) : null,
        displayName: item.displayName || item.label || '',
        columnMode: !fieldKey && formula ? 'formula' : 'field',
        fieldKey,
        formula,
        aggregateType: item.aggregateType || 'sum',
        numberFormat: item.numberFormat || 'amount',
        width: item.width ? Number(item.width) : 120,
        visible: Number(item.visible) === 1 || item.visible === true,
        sortOrder: Number(item.sortOrder || 0),
        isDimension: Number(item.isDimension) === 1 || item.isDimension === true,
        constantValue: '',
        editorKey: `saved_${item.id || ++this.formulaEditorSequence}`,
        formulaSelectionStart: null,
        formulaSelectionEnd: null
      }
    },
    readResponse(response, fallback) {
      if (!response.data || !response.data.success) {
        const msg = (response.data && (response.data.msg || response.data.message)) || fallback
        this.$message.error(msg)
        throw new Error(msg)
      }
      return response.data.data || {}
    },
    reportRowClass({ row }) {
      return row && row.__summary ? 'summary-row' : ''
    },
    columnWidth(column) {
      const labelLength = String(column.label || '').length
      if (column.numberFormat === 'date') return 130
      if (labelLength <= 4) return 110
      if (labelLength <= 8) return 140
      return 180
    },
    formatCell(value, column) {
      if (value === null || value === undefined || value === '') return '-'
      if (value === '合计') return value
      const format = column.numberFormat || 'text'
      if (format === 'date') return String(value).slice(0, 10)
      if (format === 'integer') return this.formatInteger(value)
      if (format === 'amount') return this.formatNumber(value, 2)
      if (format === 'decimal') return this.formatNumber(value, 4)
      if (format === 'percent') {
        const number = Number(value)
        return Number.isFinite(number) ? `${(number * 100).toFixed(2)}%` : value
      }
      return value
    },
    formatNumber(value, scale) {
      const number = Number(value)
      if (!Number.isFinite(number)) return value
      const fixed = number.toFixed(scale)
      const trimmed = fixed.replace(/\.?0+$/, '')
      const parts = trimmed.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    },
    formatInteger(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return value || '0'
      return Math.round(number).toLocaleString()
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    emptyPageForm() {
      return {
        id: null,
        code: '',
        pageName: '',
        datasetId: null,
        datasetIds: [],
        defaultGroupBy: [],
        defaultSortField: '',
        defaultSortDir: 'asc',
        enabled: true,
        sortOrder: 100
      }
    },
    emptyConfigForm() {
      return {
        gameName: '',
        platform: '抖音',
        packageName: '',
        remark: '',
        enabled: true
      }
    }
  }
}
</script>

<style scoped>
.project-summary-page {
  min-height: 100%;
  padding: 14px;
  background: #f3f6fb;
  color: #1f2d3d;
}

.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e5eaf3;
  border-radius: 4px;
}

.page-head h2 {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #10233f;
}

.page-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #738196;
}

.head-actions {
  display: flex;
  gap: 8px;
}

.query-panel,
.table-panel {
  margin-top: 10px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e5eaf3;
  border-radius: 4px;
}

.query-form {
  padding-top: 8px;
}

.manual-import-upload {
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  color: #303133;
}

.muted {
  color: #8792a6;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  color: #606266;
  font-size: 12px;
}

.danger-link {
  color: #f56c6c;
}

.column-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0;
  font-size: 13px;
  font-weight: 600;
}

.page-config-form {
  border-bottom: 1px solid #edf1f7;
  margin-bottom: 10px;
}

.page-config-form ::v-deep .el-row:last-child .el-col:last-child .el-form-item {
  display: flex;
  align-items: center;
}

.page-config-form ::v-deep .el-row:last-child .el-col:last-child .el-form-item__label {
  width: auto !important;
  padding-right: 8px;
}

.page-config-form ::v-deep .el-row:last-child .el-col:last-child .el-form-item__content {
  flex: 1;
  margin-left: 0 !important;
}

.dataset-config-form {
  border-bottom: 1px solid #edf1f7;
  margin-bottom: 10px;
}

.dataset-select-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-help,
.formula-help {
  margin-top: 4px;
  color: #8792a6;
  font-size: 12px;
  line-height: 18px;
}

.suffix-rule-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.suffix-rule-row {
  display: grid;
  grid-template-columns: 170px 42px 150px 32px;
  align-items: center;
  gap: 6px;
}

.dataset-field-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.dataset-field-toolbar .el-alert {
  flex: 1;
  padding: 7px 12px;
}

.formula-editor {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  width: 100%;
}

.formula-input {
  width: 100%;
}

.formula-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.formula-field-select {
  flex: 1 1 150px;
  min-width: 130px;
}

.formula-token-select {
  flex: 0 0 140px;
}

.formula-constant-input {
  flex: 0 0 190px;
}

.formula-clear-button {
  flex: 0 0 32px;
  padding: 7px;
}

.page-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.column-mode-group {
  white-space: nowrap;
}

::v-deep .column-mode-group .el-radio-button__inner {
  padding: 7px 9px;
}

::v-deep .summary-row td {
  background: #eef5ff !important;
  font-weight: 600;
}

::v-deep .el-tabs__header {
  margin-bottom: 0;
}
</style>
